import React from 'react';
import { FileText, LayoutGrid, Calendar, Settings, Lock, ChevronRight, Briefcase, MessageSquare, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useUserApplication } from '../../../context/UserApplicationContext';
import { getStatusLabel } from '../../../utils/applicationStatus';

const MARKOTM_ORG_ID = process.env.REACT_APP_MARKOTM_ORG_ID;

/**
 * Dashboard sidebar navigation component
 * Clean, modern design with brand colors
 *
 * Desktop (md+): static 256px column, always visible (unchanged).
 * Mobile (<md): overlay drawer under the fixed header, opened via the
 * hamburger in DashboardHeader; backdrop click closes it.
 *
 * @param {Object} props
 * @param {string} props.activeSection - Currently active section ID
 * @param {Function} props.onSectionChange - Callback when section is changed
 * @param {boolean} props.mobileOpen - Whether the mobile drawer is open
 * @param {Function} props.onClose - Close the mobile drawer
 * @returns {JSX.Element}
 */
const Sidebar = ({ activeSection, onSectionChange, mobileOpen = false, onClose }) => {
  const { currentApplication } = useUserApplication();
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { organization } = useOrganization();

  // Check if user is part of MarkoTM organization (internal staff)
  const isInternalUser = organization?.id === MARKOTM_ORG_ID;
  
  const sections = [
    {
      id: 'home',
      label: t('sidebar.dashboard', 'Dashboard'),
      description: t('sidebar.overview', 'Overview'),
      icon: LayoutGrid,
    },
    {
      id: 'payments',
      label: t('sidebar.quoteAndPayment', 'Quote & Payment'),
      description: t('sidebar.quoteAndPaymentDesc', 'Review your quote and pay'),
      icon: CreditCard,
    },
    {
      id: 'portfolio',
      label: t('sidebar.myPortfolio', 'My Portfolio'),
      description: t('sidebar.viewTrademarks', 'View your trademarks'),
      icon: FileText,
    },
    {
      id: 'messages',
      label: t('sidebar.messages', 'Messages'),
      description: t('sidebar.attorneyMessages', 'Attorney communications'),
      icon: MessageSquare,
    },
    {
      id: 'renewals',
      label: t('sidebar.renewals', 'Renewals'),
      description: t('sidebar.upcomingDeadlines', 'Upcoming deadlines'),
      icon: Calendar,
      comingSoon: true
    },
  ];

  return (
    <>
    {/* Mobile backdrop — click closes the drawer */}
    {mobileOpen && (
      <div
        className="fixed inset-0 top-16 z-30 bg-black/40 md:hidden touch-none"
        onClick={onClose}
        aria-hidden="true"
      />
    )}
    <div className={`bg-white border-r border-gray-200 shadow-sm w-64 flex-shrink-0 tutorial-sidebar flex flex-col overflow-y-auto
      fixed top-16 bottom-0 left-0 z-40 transform transition-transform duration-200 ease-in-out
      ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      md:static md:h-full md:z-auto md:translate-x-0 md:transition-none`}
    >
      {/* Current Application Card */}
      {currentApplication && (
        <div className="p-4 border-b border-gray-100">
          <div className="bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb] rounded-xl p-4">
            <p className="text-xs font-medium text-[#004d40] uppercase tracking-wide mb-1">{t('sidebar.currentApplication', 'Current Application')}</p>
            <h3 className="font-semibold text-[#004d40] truncate">
              {currentApplication.name || currentApplication.trademarkName || 'New Application'}
            </h3>
            <p className="text-xs text-[#00796b] mt-1">
              {getStatusLabel(currentApplication.status, t)}
            </p>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <nav className="flex-1 p-3">
        <ul className="space-y-1">
          {sections.map(section => {
            const isActive = activeSection === section.id;
            const Icon = section.icon;
            
            return (
              <li key={section.id}>
                <button
                  onClick={() => !section.comingSoon && onSectionChange(section.id)}
                  disabled={section.comingSoon}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200
                    ${isActive 
                      ? 'bg-[#004d40] text-white shadow-md' 
                      : section.comingSoon
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className={`
                    w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                    ${isActive 
                      ? 'bg-white/20' 
                      : section.comingSoon
                        ? 'bg-gray-100'
                        : 'bg-[#e0f2f1]'
                    }
                  `}>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : section.comingSoon ? 'text-gray-400' : 'text-[#00796b]'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                        {section.label}
                      </span>
                      {section.comingSoon && (
                        <Lock className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                    {!section.comingSoon && (
                      <p className={`text-xs truncate ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                        {section.description}
                      </p>
                    )}
                  </div>
                  
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-white/70" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      
      {/* Footer */}
      <div className="p-4 border-t border-gray-100 space-y-1">
        {/* Attorney Dashboard - show for internal users */}
        {isInternalUser && (
          <button
            onClick={() => navigate('/attorney')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#00796b] hover:bg-[#e0f2f1] transition-colors"
          >
            <Briefcase className="w-4 h-4" />
            <span className="text-sm font-medium">{t('sidebar.attorneyDashboard', 'Attorney Dashboard')}</span>
          </button>
        )}
        <button
          onClick={() => onSectionChange('settings')}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <Settings className="w-4 h-4" />
          <span className="text-sm">{t('sidebar.settings', 'Settings')}</span>
        </button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
